import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";

import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Filter,
  Group,
  Inject,
  Toolbar,
  ExcelExport,
  PdfExport,
  Resize,
  TextWrapSettingsModel,
} from "@syncfusion/ej2-react-grids";
import { ClickEventArgs } from "@syncfusion/ej2-navigations";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllBusinessUnits } from "../../redux/features/business-unit/business-unit";

import { getTenantClientInsurances } from "../../service/client.service";

import moment from "moment";

const ClientInsurancesList: React.FC = () => {
  const [insurances, setInsurances] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const PAGE_SIZE = 15;

  const [statusFilter, setStatusFilter] = useState<
    "All" | "active" | "expired" | "upcoming"
  >("active"); 
  const [buFilter, setBuFilter] = useState<string>("");

  const dispatch = useAppDispatch();
  const businessUnits = useAppSelector(
    (state) => state.businessUnitsSlice.businessUnits
  );

  const buNameById = useMemo(() => {
    const map: Record<string, string> = {};
    businessUnits.forEach((bu) => {
      map[bu.id] = bu.businessUnitName;
    });
    return map;
  }, [businessUnits]);

  const gridInstance = React.useRef<GridComponent>(null);

  const textWrapSettings: TextWrapSettingsModel = { wrapMode: "Content" };

  const fetchInsurances = async (status: string, buId: string) => {
    try {
      setLoading(true);
      
      const statusParam = status === "All" ? "" : status;
      
      const response = await getTenantClientInsurances({
        status: statusParam,
        buId: buId || undefined
      });
      
      if (response?.data) {
        setInsurances(response.data);
      }
    } catch (error) {
      console.error("Error fetching client insurances:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getAllBusinessUnits());
    fetchInsurances("active", "");
  }, [dispatch]);

  useEffect(() => {
    if (!loading) {
      fetchInsurances(statusFilter, buFilter);
    }
  }, [statusFilter, buFilter]);

  const toolbarClick = (args: ClickEventArgs) => {
    if (gridInstance.current) {
      if (args.item.id === "grid_pdfexport") {
        gridInstance.current.showSpinner();
        gridInstance.current.pdfExport();
      } else if (args.item.id === "grid_excelexport") {
        gridInstance.current.showSpinner();
        gridInstance.current.excelExport();
      }
    }
  };

  const formatDate = (date: string | Date) => {
    if (!date) return "-";
    return moment(date).format("MM/DD/YYYY");
  };

  // Build enriched data so Syncfusion filtering operates on concrete properties
  const gridData = useMemo(() => {
    return insurances.map((item) => {
      const client = item.client ?? {};
      const clientName =
        `${client.childFirstName ?? ""} ${client.childLastName ?? ""}`.trim() ||
        "-";

      const buArr: string[] = client.businessUnit || [];
      const buNames = buArr.length
        ? buArr.map((id: string) => buNameById[id] || id).join(", ")
        : "-";

      return {
        ...item,
        clientName,
        buNames,
        providerName: item.insurance?.insuranceProviderName || "-",
        effectiveDateDisplay: formatDate(item.policyEffectiveDate),
        expirationDateDisplay: formatDate(item.expirationDate),
        isPrimaryDisplay: item.isPrimary ? "Yes" : "No",
        isSecondaryDisplay: !item.isPrimary ? "Yes" : "No",
      };
    });
  }, [insurances, buNameById]);

  return (
    <Box sx={{ p: 3 }}>
      {}
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          mb: 2 
        }}
      >
        <Typography variant="h6" className="DetailsPageTitle">
          Client Insurances
        </Typography>

        {/* Filters aligned to the right */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {/* Status filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) =>
                setStatusFilter(e.target.value as typeof statusFilter)
              }
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
            </Select>
          </FormControl>

          {/* Business Unit filter */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="bu-filter-label">Business Unit</InputLabel>
            <Select
              labelId="bu-filter-label"
              value={buFilter}
              label="Business Unit"
              onChange={(e) => setBuFilter(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              {businessUnits.map((bu) => (
                <MenuItem key={bu.id} value={bu.id}>
                  {bu.businessUnitName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={2} sx={{ p: 2 }}>
          <GridComponent
            id="insurancesGrid"
            dataSource={gridData}
            allowPaging={true}
            pageSettings={{ pageSize: PAGE_SIZE }}
            allowSorting={true}
            allowFiltering={true}
            filterSettings={{ type: "Menu" }}
            allowResizing={true}
            allowTextWrap={true}
            textWrapSettings={textWrapSettings}
            toolbarClick={toolbarClick}
            ref={gridInstance}
          >
            <ColumnsDirective>
              {/* Client column */}
              <ColumnDirective
                field="clientName"
                headerText="Client"
                width="180"
                valueAccessor={(field: string, data: any) => {
                  if (!data) return "-";
                  if (!data.client) return "-";
                  const { childFirstName = "", childLastName = "" } = data.client;
                  return `${childFirstName} ${childLastName}`.trim() || "-";
                }}
                template={(rowData: any) => {
                  if (!rowData.client) return "-";
                  const { childFirstName = "", childLastName = "" } = rowData.client;
                  return `${childFirstName} ${childLastName}`.trim() || "-";
                }}
              />
              {/* Business Unit(s) column */}
              <ColumnDirective
                field="buNames"
                headerText="BU"
                width="200"
                valueAccessor={(field: string, data: any) => {
                  const buArr: string[] = data?.client?.businessUnit || [];
                  return buArr.length
                    ? buArr.map((id: string) => buNameById[id] || id).join(", ")
                    : "-";
                }}
                template={(rowData: any) => {
                  const buArr: string[] = rowData?.client?.businessUnit || [];
                  if (!buArr.length) return "-";
                  return buArr
                    .map((id: string) => buNameById[id] || id)
                    .join(", ");
                }}
              />
              {/* Provider column */}
              <ColumnDirective
                field="providerName"
                headerText="Provider"
                width="180"
                valueAccessor={(field: string, data: any) => {
                  if (!data) return "-";
                  return data.insurance?.insuranceProviderName || "-";
                }}
                template={(rowData: any) => {
                  if (!rowData.insurance) return "-";
                  return rowData.insurance.insuranceProviderName || "-";
                }}
              />
              {/* Effective Date column */}
              <ColumnDirective
                field="policyEffectiveDate"
                headerText="Effective Date"
                width="150"
                type="date"
                format="MM/dd/yyyy"
                template={(rowData: any) => formatDate(rowData.policyEffectiveDate)}
              />
              {/* Expiration Date column */}
              <ColumnDirective
                field="expirationDate"
                headerText="Expiration Date"
                width="150"
                type="date"
                format="MM/dd/yyyy"
                template={(rowData: any) => formatDate(rowData.expirationDate)}
              />
              {/* Primary column */}
              <ColumnDirective
                field="isPrimaryDisplay"
                headerText="Primary"
                width="120"
                valueAccessor={(field: string, data: any) =>
                  data && data.isPrimary ? "Yes" : "No"
                }
                template={(rowData: any) => {
                  return rowData.isPrimary ? "Yes" : "No";
                }}
              />
              {/* Secondary column */}
              <ColumnDirective
                field="isSecondaryDisplay"
                headerText="Secondary"
                width="120"
                valueAccessor={(field: string, data: any) =>
                  data && !data.isPrimary ? "Yes" : "No"
                }
                template={(rowData: any) => {
                  return !rowData.isPrimary ? "Yes" : "No";
                }}
              />              
            </ColumnsDirective>
            <Inject
              services={[
                Page,
                Sort,
                Filter,
                Group,
                Toolbar,
                ExcelExport,
                PdfExport,
                Resize,
              ]}
            />
          </GridComponent>
        </Paper>
      )}
    </Box>
  );
};

export default ClientInsurancesList;
